import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import ManagerLayout from "@/components/ManagerLayout";
import { Plus, Edit, Trash2, UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const ManagerPlayers = () => {
  const [players, setPlayers] = useState([]);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [formData, setFormData] = useState({
    name: "", position: "", number: "", nationality: "Antigua and Barbuda",
    photo_url: "", date_of_birth: "", height: "", weight: ""
  });

  const fetchData = async () => {
    try {
      const [teamRes, playersRes] = await Promise.all([
        axios.get(`${API}/manager/my-team`, { withCredentials: true }),
        axios.get(`${API}/manager/my-players`, { withCredentials: true })
      ]);
      setTeam(teamRes.data);
      setPlayers(playersRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        team_id: team.team_id,
        number: formData.number ? parseInt(formData.number) : null
      };

      if (editingPlayer) {
        await axios.put(`${API}/players/${editingPlayer.player_id}`, payload, { withCredentials: true });
        toast.success("Player updated");
      } else {
        await axios.post(`${API}/players`, payload, { withCredentials: true });
        toast.success("Player added to squad");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save player");
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      name: player.name || "",
      position: player.position || "",
      number: player.number || "",
      nationality: player.nationality || "Antigua and Barbuda",
      photo_url: player.photo_url || "",
      date_of_birth: player.date_of_birth || "",
      height: player.height || "",
      weight: player.weight || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (playerId) => {
    if (!window.confirm("Remove this player from the squad?")) return;
    try {
      await axios.delete(`${API}/players/${playerId}`, { withCredentials: true });
      toast.success("Player removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove player");
    }
  };

  const resetForm = () => {
    setEditingPlayer(null);
    setFormData({ name: "", position: "", number: "", nationality: "Antigua and Barbuda", photo_url: "", date_of_birth: "", height: "", weight: "" });
  };

  const POS_ALIASES = { GK: "GK", GOALKEEPER: "GK", KEEPER: "GK", DEF: "DEF", DEFENDER: "DEF", DF: "DEF", MID: "MID", MIDFIELDER: "MID", MF: "MID", FWD: "FWD", FORWARD: "FWD", STRIKER: "FWD", FW: "FWD", ATT: "FWD" };
  const posOf = (p) => POS_ALIASES[(p.position || "").toUpperCase().trim()] || "OTHER";
  const grouped = { GK: [], DEF: [], MID: [], FWD: [], OTHER: [] };
  players.forEach((p) => grouped[posOf(p)].push(p));

  const positionLabel = { GK: 'Goalkeepers', DEF: 'Defenders', MID: 'Midfielders', FWD: 'Forwards', OTHER: 'Position Not Set' };

  return (
    <ManagerLayout title="Team Roster">
      <div data-testid="manager-players">
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-500">{players.length} players in squad</p>
          <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="add-player-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Player
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold uppercase">
                  {editingPlayer ? "Edit Player" : "Add New Player"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="player-form">
                <div>
                  <label className="text-sm font-medium">Full Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required className="rounded-none" data-testid="player-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Position *</label>
                    <Select value={formData.position} onValueChange={(v) => setFormData({...formData, position: v})}>
                      <SelectTrigger className="rounded-none" data-testid="player-position-select">
                        <SelectValue placeholder="Position" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GK">Goalkeeper</SelectItem>
                        <SelectItem value="DEF">Defender</SelectItem>
                        <SelectItem value="MID">Midfielder</SelectItem>
                        <SelectItem value="FWD">Forward</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Jersey Number</label>
                    <Input
                      type="number"
                      value={formData.number}
                      onChange={(e) => setFormData({...formData, number: e.target.value})}
                      className="rounded-none" data-testid="player-number-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Nationality</label>
                  <Input
                    value={formData.nationality}
                    onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Photo URL</label>
                  <Input
                    value={formData.photo_url}
                    onChange={(e) => setFormData({...formData, photo_url: e.target.value})}
                    className="rounded-none" placeholder="https://..."
                  />
                </div>
                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="player-submit-btn">
                    {editingPlayer ? "Update Player" : "Add Player"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading roster...</p>
        ) : players.length > 0 ? (
          <div className="space-y-8">
            {Object.entries(grouped).map(([pos, posPlayers]) => (
              posPlayers.length > 0 && (
                <div key={pos}>
                  <h3 className="font-heading font-bold text-lg uppercase text-[#09090b] mb-4 border-l-4 border-[#FFC72C] pl-3">
                    {positionLabel[pos]} ({posPlayers.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {posPlayers.map(player => (
                      <div key={player.player_id} className="bg-white border border-gray-200 overflow-hidden group" data-testid={`player-card-${player.player_id}`}>
                        <div className="aspect-square bg-gray-100 relative overflow-hidden">
                          <img 
                            src={player.photo_url || 'https://placehold.co/300x300/09090b/FFF?text=PLAYER'} 
                            alt={player.name}
                            className="w-full h-full object-cover"
                          />
                          {player.number && (
                            <span className="absolute top-2 right-2 bg-[#FFC72C] text-black font-heading font-bold px-2 py-0.5">
                              #{player.number}
                            </span>
                          )}
                        </div>
                        <div className="p-3">
                          <h4 className="font-heading font-bold uppercase text-sm truncate">{player.name}</h4>
                          <p className="text-xs text-gray-500">{player.position}</p>
                          <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(player)} className="flex-1 rounded-none h-7 text-xs" data-testid={`edit-player-${player.player_id}`}>
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(player.player_id)} className="rounded-none h-7 text-xs" data-testid={`delete-player-${player.player_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-gray-200">
            <UserCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-gray-400 uppercase mb-2">No Players Yet</h3>
            <p className="text-gray-500">Add your first player to build your squad.</p>
          </div>
        )}
      </div>
    </ManagerLayout>
  );
};

export default ManagerPlayers;
